import {inject, Injectable} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {catchError, Observable, map} from "rxjs";
import {removeEmptyParams} from "src/infrastructure/helper/remove-empty-params";
import {VendorBalenceResponse} from "../models/vendor-balances";
import moment from 'moment';
import {GridGetService, GridServiceParams, GridServiceResponse} from "src/app/shared/model/page-grid";
import {ExportPdfService} from "src/app/shared/service/files/export-pdf.service";

@Injectable({
  providedIn: 'root',
})
export class VendorBalanceService implements GridGetService<VendorBalenceResponse> {

  private readonly baseUrl = 'dbov2/vendor-balances';
  private readonly http = inject(HttpClient);
  private readonly pdfService = inject(ExportPdfService);

  private extractFilterValue(val: any): string | number | null {
  if (val && typeof val === 'object') {
    if ('id' in val) return val.id;
    if ('value' in val) return val.value;
  }
  if (typeof val === 'string' && val.trim() !== '') return val;
  if (typeof val === 'number') return val;
  return null;
}

  private getAutocompleteValue(value: any): string {
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }
    return value || '';
  }

  getItems({ filters, page, sort }: GridServiceParams): Observable<GridServiceResponse<VendorBalenceResponse>> {
    const params: Record<string, string | number | boolean> = {
      pageSize: page?.pageSize || 25,
      pageNumber: (page?.pageIndex || 0) + 1,
      propertyName: sort?.active || '',
      orderKey: sort?.direction || '',
      asOfDate: filters?.['asOfDate'] ? moment(filters?.['asOfDate']).format('YYYY-MM-DD') : '',
      paymentDateFrom: filters?.['paymentDateFrom'] ? moment(filters?.['paymentDateFrom']).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
      paymentDateTo: filters?.['paymentDateTo'] ? moment(filters?.['paymentDateTo']).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
      vendorName: this.getAutocompleteValue(filters?.['vendorName']),
      minOutstandingBalance: filters?.['minOutstandingBalance'] ?? '',
      maxOutstandingBalance: filters?.['maxOutstandingBalance'] ?? '',
      minTotalBilled: filters?.['minTotalBilled'] ?? '',
      maxTotalBilled: filters?.['maxTotalBilled'] ?? '',
      dataSourceId: filters?.['dataSourceId'] ?? '',
      vendorPayStatusId: filters?.['vendorPayStatusId'] ?? '',
    };

    const vendorId = this.extractFilterValue(filters?.['id']);
    if (vendorId !== null) {
      params['id'] = vendorId;
    }

    removeEmptyParams(params);

    return this.http.get<GridServiceResponse<VendorBalenceResponse>>(`${this.baseUrl}`,
      { params: { ...params } }
    ).pipe(catchError((error: HttpErrorResponse) => {
      throw new Error(error?.message || 'Server error');
    }));
  }

  getPdf({ filters, page, sort }: GridServiceParams): Observable<void> {
    const params: Record<string, string | number | boolean> = {
      exportToPdf: true,
      format: 'pdf',
      pageSize: page?.pageSize || 100,
      propertyName: sort?.active || '',
      orderKey: sort?.direction || '',      asOfDate: filters?.['asOfDate'] ? moment(filters?.['asOfDate']).format('YYYY-MM-DD') : '',
      paymentDate: filters?.['paymentDate'] ? moment(filters?.['paymentDate']).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
      vendorName: this.getAutocompleteValue(filters?.['vendorName']),
      minOutstandingBalance: filters?.['minOutstandingBalance'] ?? '',
      maxOutstandingBalance: filters?.['maxOutstandingBalance'] ?? '',
      minTotalBilled: filters?.['minTotalBilled'] ?? '',
      maxTotalBilled: filters?.['maxTotalBilled'] ?? '',
      dataSourceId: filters?.['dataSourceId'] ?? '',
      vendorPayStatusId: filters?.['vendorPayStatusId'] ?? '',
    };

    removeEmptyParams(params);

    return this.http.get<{ pdfBase64: string }>(`${this.baseUrl}`, {
      params: { ...params }
    }).pipe(
      map(response => {
        if (response && response.pdfBase64) {
          this.pdfService.savePdfFromBase64(response.pdfBase64, 'vendor-balance.pdf');
          return;
        } else {
          throw new Error('Invalid PDF response from server');
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('PDF download error:', error);
        throw new Error(error?.message || 'PDF generation failed');
      })
    );
  }
}

